<script lang="ts">
import { defineComponent } from 'vue'
import { debounce } from 'lodash'
import type { BookstackSearchResult, BookstackSearchInstance } from '../types/bookstackTypes'
import { bookstackQuery } from '../utils/bookstack'

export default defineComponent({
  name: 'BookstackSearch',
  data() {
    return {
      search: '',
      loading: false,
      searchResults: [] as BookstackSearchResult[],
      previewCount: 3
    }
  },
  watch: {
    async search(newValue) {
      if (newValue.length <= 2) {
        this.searchResults = []
        return
      }

      this.performSearch(newValue)
    }
  },
  methods: {
    performSearch: debounce(async function (this: BookstackSearchInstance, query: string) {
      this.loading = true
      this.searchResults = await this.bookstackQuery(query, { count: this.previewCount })
      this.loading = false
    }, 300),
    bookstackQuery
  },
  computed: {
    searchItems() {
      const ret = []
      for (const result of this.searchResults) {
        const hits = result.response ?? []
        if (hits.length === 0) continue

        ret.push({ type: 'subheader', title: `${result.source}:` })
        for (const hit of hits.slice(0, this.previewCount)) {
          ret.push({
            title: hit.name,
            value: hit.id,
            href: hit.url
          })
        }

        ret.push({ type: 'divider' })
      }

      if (ret.length > 0)
        ret.push({
          title: 'See all results',
          value: 'all',
          href: '/docs?search=' + encodeURIComponent(this.search),
          target: '_self'
        })

      return ret
    },
    totalResults() {
      return this.searchResults.flatMap((source) => source?.response ?? []).length
    }
  }
})
</script>

<template>
  <v-text-field
    v-model="search"
    type="search"
    placeholder="Search Docs"
    variant="solo"
    density="compact"
    bg-color="white"
    hide-details
    style="width: 100%; max-width: 250px"
    :loading="loading"
  >
    <template v-slot:append-inner>
      <v-icon icon="fa-solid fa-search" />
    </template>

    <v-menu
      activator="parent"
      :style="{
        display: totalResults > 0 ? 'block' : 'none'
      }"
    >
      <v-list>
        <!-- nasty custom rendering because for some reason, item.hrefs are not respected... -->
        <template v-for="(item, index) in searchItems" :key="index">
          <v-list-subheader v-if="item.type === 'subheader'">{{ item.title }}</v-list-subheader>
          <v-divider v-else-if="item.type === 'divider'" />
          <v-list-item v-else :href="item.href" :target="item.target ?? '_blank'" link>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </template>
      </v-list>
    </v-menu>
  </v-text-field>
</template>

<style scoped></style>
